import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import InputField from "../components/inputFields/InputField";
import TextField from "../components/inputFields/TextField";
import { FiUser, FiTag, FiCopy } from "react-icons/fi";
import FormikDropdown from "../components/inputFields/FormikDropDown";
import Button from "../components/buttons/Button";
import {
    getSupportTicketUserConfirmedOrders,
    getSupportTicketsTimeline,
    searchSupportAssignableUsers,
    supportTicketTimelinePost
} from "../services/supportTickets";
import { createDashboardTicket, updateDashboardTicket } from "../services/dashboardTickets";
import { toast } from "react-toastify";
import CustomLoading from "../components/customLoading/CustomLoading";
import { getUserId } from "../helpers/local_storage_helpers";
import * as Yup from "yup";
import AsyncFormikDropdown from "../components/inputFields/AsyncFormikDropDown";
import { searchUser } from "../services/search";
import { BASE_URL } from "../constants/apis";
import { sanitizeMobile } from "../utils/phone";

const TICKET_CATEGORY_OPTIONS = [
    { label: "App Issue", value: "app_issue" },
    { label: "Order Issue", value: "order_issue" },
    { label: "Device Issue", value: "device_issue" },
    { label: "Device Installation", value: "device_installation" },
    { label: "Payment Issue", value: "payment_issue" },
    { label: "Delivery Issue", value: "delivery_issue" },
    { label: "Other", value: "other" },
];

const normalizeStatusValue = (status) =>
    String(status || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");

const normalizePriorityForm = (p) => {
    const s = String(p || "")
        .trim()
        .toLowerCase();
    if (["low", "medium", "high"].includes(s)) return s;
    return "low";
};

const toDatetimeLocalValue = (isoOrDate) => {
    if (!isoOrDate) return "";
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const resolutionComparable = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? String(raw) : d.toISOString();
};

const truncateOneLine = (text, max = 120) => {
    const s = String(text || "").trim();
    if (!s) return "";
    return s.length <= max ? s : `${s.slice(0, max)}…`;
};

const TICKET_PRIORITY_OPTIONS = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "medium" },
    { label: "High", value: "high" },
];

/** Resolve staff display name from timeline API (field names vary by backend). */
const getTimelineStaffName = (item) => {
    if (!item || typeof item !== "object") return "—";
    const raw =
        item.staff_name ??
        item.created_by_name ??
        item.author_name ??
        item.user_name ??
        item.added_by_name ??
        (item.staff && typeof item.staff === "object" ? item.staff.name : null) ??
        (typeof item.staff === "string" ? item.staff : null) ??
        item.created_by;
    const s = raw != null ? String(raw).trim() : "";
    return s || "—";
};

const SupportTicketDetails = ({ ticket, onOperation }) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [ticketData, setTicketData] = useState(ticket || {});
    const [loading, setLoading] = useState(false);
    const [timelineData, setTimelineData] = useState([]);
    const [isExistingUser, setIsExistingUser] = useState(true);
    const [orderOptions, setOrderOptions] = useState([]);
    const [orderItemOptions, setOrderItemOptions] = useState([]);
    const [uploadedImagePreview, setUploadedImagePreview] = useState("");
    const [copiedId, setCopiedId] = useState(false);

    const copyTicketId = async () => {
        const id = ticketData?.id;
        if (!id) return;
        try {
            await navigator.clipboard.writeText(String(id));
            setCopiedId(true);
            toast.success("Ticket ID copied");
            setTimeout(() => setCopiedId(false), 2000);
        } catch {
            toast.error("Could not copy ID");
        }
    };

    const handleAddComment = async (comment, { resetForm, setSubmitting }) => {
        const trimmed = String(comment || "").trim();
        if (!trimmed) {
            toast.error("Please enter a comment.");
            setSubmitting(false);
            return;
        }
        const newComment = {
            ticket: ticketData.id,
            comment: trimmed,
            status: normalizeStatusValue(ticketData.status) || "pending",
            staff: getUserId(),
        };
        try {
            const res = await supportTicketTimelinePost(newComment);
            if (res.status === 201) {
                toast.success("Comment added successfully");
                fetchTicketDetails(ticketData.id);
                resetForm();
            } else {
                toast.error("Unable to add comment");
            }
        } catch {
            toast.error("Unable to add comment");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        setUploadedImagePreview("");
    }, [ticket?.id]);

    useEffect(() => {
        if (ticket) {
            setTicketData(ticket);
            const imgRaw = ticket.image;
            const hasImg = imgRaw != null && String(imgRaw).trim() !== "";
            if (hasImg) {
                const src = String(imgRaw).startsWith("http")
                    ? imgRaw
                    : `${BASE_URL}${imgRaw}`;
                setSelectedImage(src);
            } else {
                setSelectedImage(null);
            }
        } else {
            setSelectedImage(null);
        }
    }, [ticket]);


    const fetchTicketDetails = async (id) => {
        setLoading(true);
        try {
            let res = await getSupportTicketsTimeline(id);
            if (res.status === 200) {
                const data = res.data;
                setTimelineData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching Data:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (ticket?.id) {
            fetchTicketDetails(ticket.id);
        }
    }, [ticket?.id]);


    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const s = normalizeStatusValue(status);
        if (s === "resolved") return "bg-green-100 text-green-800";
        if (s === "pending") return "bg-yellow-100 text-yellow-800";
        if (s === "closed") return "bg-slate-100 text-slate-800";
        if (s === "reopened" || s === "open" || s === "ticket_created")
            return "bg-blue-100 text-blue-800";
        return "bg-gray-100 text-gray-800";
    };

    const fetchConfirmedOrdersByUser = async (userId, preselectedOrderId = null) => {
        if (!userId) {
            setOrderOptions([]);
            setOrderItemOptions([]);
            return;
        }
        try {
            const response = await getSupportTicketUserConfirmedOrders(userId);
            if (response?.status === 200) {
                const userOrders = response?.data?.user_order || [];
                const normalizedOrders = userOrders.map((order) => ({
                    value: order.id,
                    label: `${String(order.id).slice(0, 8)}... | ${order.order_date} | ${order.status}`,
                    orderItems: order.order_items || [],
                }));
                setOrderOptions(normalizedOrders);
                if (preselectedOrderId) {
                    const selectedOrder = normalizedOrders.find(
                        (o) => String(o.value) === String(preselectedOrderId)
                    );
                    const mappedOrderItems = (selectedOrder?.orderItems || []).map((item) => ({
                        value: item.id,
                        label: `${item.product_name} | Qty: ${item.quantity}`,
                    }));
                    setOrderItemOptions(mappedOrderItems);
                } else {
                    setOrderItemOptions([]);
                }
            } else {
                setOrderOptions([]);
                setOrderItemOptions([]);
            }
        } catch (error) {
            setOrderOptions([]);
            setOrderItemOptions([]);
            toast.error("Unable to fetch confirmed orders for selected user");
        }
    };

    const updateOrderItemsByOrderId = (orderId) => {
        const selectedOrder = orderOptions.find(
            (order) => String(order.value) === String(orderId)
        );
        const mappedOrderItems = (selectedOrder?.orderItems || []).map((item) => ({
            value: item.id,
            label: `${item.product_name} | Qty: ${item.quantity}`,
        }));
        setOrderItemOptions(mappedOrderItems);
    };

    useEffect(() => {
        if (ticket?.user_id) {
            fetchConfirmedOrdersByUser(ticket.user_id, ticket.order_id || null);
        } else {
            setOrderOptions([]);
            setOrderItemOptions([]);
        }
    }, [ticket?.id, ticket?.user_id, ticket?.order_id]);

    const assigneeInitialOptions = useMemo(() => {
        if (!ticket?.id) return [];
        const id = ticketData?.assigned_to_id ?? ticket?.assigned_to_id;
        if (id === undefined || id === null || id === "") return [];
        const label =
            ticketData?.assigned_to ?? ticket?.assigned_to ?? `Staff #${id}`;
        return [{ value: id, label }];
    }, [ticket?.id, ticketData?.assigned_to_id, ticketData?.assigned_to]);

    const mapTicketPayload = (values) => {
        const assignedById = getUserId();
        const payload = {
            category: values.category || "",
            category_other: values.category === "other" ? (values.category_other || "") : "",
            priority: values.priority || "low",
            subject: values.subject || "",
            description: values.description ?? "",
            assigned_by: assignedById,
            assigned_to:
                values.assigned_to !== "" && values.assigned_to != null
                    ? values.assigned_to
                    : "",
            order_id: values.order_id || values.order || "",
            order_item_id: values.order_item_id || values.order_item || "",
            resolution_date_time_given: values.time_slot_assigned
                ? new Date(values.time_slot_assigned).toISOString()
                : "",
        };

        if (!ticket?.id) {
            if (isExistingUser) {
                payload.user = values.user || "";
            } else {
                payload.new_user_name = values.new_user_name || "";
                payload.new_user_phone_number = values.new_user_phone_number || "";
            }
        }

        if (values.image instanceof File) {
            payload.image = values.image;
        }

        return payload;
    };

    const buildChangedFieldsForPatch = (values) => {
        if (!ticket?.id) return mapTicketPayload(values);
        const mapped = mapTicketPayload(values);
        const initialMapped = {
            category: ticketData?.category || "",
            category_other:
                ticketData?.category === "other" ? (ticketData?.category_other || "") : "",
            priority: normalizePriorityForm(ticketData?.priority),
            subject: ticketData?.subject || "",
            description: ticketData?.description ?? "",
            assigned_to:
                ticketData?.assigned_to_id !== undefined &&
                ticketData?.assigned_to_id !== null
                    ? ticketData.assigned_to_id
                    : "",
            order_id: ticketData?.order_id || "",
            order_item_id: ticketData?.order_item_id || "",
            resolution_date_time_given: resolutionComparable(
                ticketData?.resolution_date_time_given
            ),
            assigned_by: getUserId(),
        };

        const changed = {};
        Object.keys(mapped).forEach((key) => {
            if (key === "image") return;
            if (String(mapped[key] ?? "") !== String(initialMapped[key] ?? "")) {
                changed[key] = mapped[key];
            }
        });
        if (values.image instanceof File) {
            changed.image = values.image;
        }
        return changed;
    };

    const handleSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            const payload = buildChangedFieldsForPatch(values);
            if (ticket?.id && Object.keys(payload).length === 0) {
                toast.info("No changes to save.");
                setSubmitting(false);
                return;
            }
            const response = ticket?.id
                ? await updateDashboardTicket(ticket.id, payload)
                : await createDashboardTicket(payload);

            if (response?.status === 201 || response?.status === 200) {
                toast.success(ticket?.id ? "Ticket updated successfully." : "Ticket created successfully.");
                onOperation();
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            const data = error?.response?.data;
            if (error?.response?.status === 400 && data && typeof data === "object") {
                const backendToFormField = {
                    resolution_date_time_given: "time_slot_assigned",
                    order: "order_id",
                    order_item: "order_item_id",
                    order_id: "order_id",
                    order_item_id: "order_item_id",
                };
                const formikErrors = {};
                Object.keys(data).forEach((key) => {
                    const fieldError = data[key];
                    const formField = backendToFormField[key] || key;
                    formikErrors[formField] = Array.isArray(fieldError) ? fieldError[0] : String(fieldError);
                });
                setErrors(formikErrors);
                toast.error("Please fix validation errors.");
            } else {
                let errors =
                    data?.error ||
                    "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        }
        setSubmitting(false);
    }

    const ticketMetaLineParts = useMemo(() => {
        const parts = [];
        if (ticketData?.created_at) {
            parts.push({
                key: "created",
                label: "Created",
                value: formatDate(ticketData.created_at),
                valueClass: "font-medium text-sky-800",
            });
        }
        if (ticketData?.updated_at) {
            parts.push({
                key: "updated",
                label: "Updated",
                value: formatDate(ticketData.updated_at),
                valueClass: "font-medium text-violet-800",
            });
        }
        if (ticketData?.assigned_by) {
            parts.push({
                key: "assigned",
                label: "Assigned by",
                value: ticketData.assigned_by,
                valueClass: "font-semibold text-slate-800",
            });
        }
        if (ticketData?.order_item_name) {
            parts.push({
                key: "orderitem",
                label: "Order item",
                value: ticketData.order_item_name,
                valueClass: "font-semibold text-emerald-900",
            });
        }
        parts.push({
            key: "lastnote",
            label: "Last note",
            value: ticketData?.last_comment?.trim()
                ? truncateOneLine(ticketData.last_comment, 100)
                : "—",
            valueClass: "text-slate-600",
        });
        return parts;
    }, [ticketData]);

    return (
        <>
            <div className="p-2">
                {/* Ticket header: customer, phone, status + one-line meta */}
                {ticket && (
                    <div className="mb-3 border-b border-gray-100 pb-2">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg font-semibold tracking-tight text-gray-900">
                                    {ticketData?.user || ticketData?.new_user_name || "—"}
                                </h2>
                                <p className="mt-0.5 text-xs tabular-nums text-gray-600">
                                    {ticketData?.phone_number ||
                                        ticketData?.new_user_phone_number ||
                                        "—"}
                                </p>
                            </div>
                            <span
                                className={`inline-flex w-fit shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(ticketData.status)}`}
                            >
                                {ticketData?.status || "—"}
                            </span>
                        </div>
                        {ticketData?.id ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                                <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-800">
                                    ID
                                </span>
                                <div className="flex min-w-0 max-w-full flex-1 overflow-hidden rounded-md border border-emerald-200/90 bg-emerald-50/60 sm:max-w-xl">
                                    <input
                                        type="text"
                                        readOnly
                                        aria-label="Ticket ID"
                                        value={ticketData.id}
                                        className="min-w-0 flex-1 border-0 bg-transparent px-2 py-1.5 font-mono text-[10px] text-slate-800 focus:outline-none focus:ring-0"
                                    />
                                    <button
                                        type="button"
                                        onClick={copyTicketId}
                                        className="inline-flex shrink-0 items-center gap-0.5 border-l border-emerald-200/90 bg-primary px-2 py-1.5 text-[10px] font-bold text-white hover:bg-green-700"
                                    >
                                        <FiCopy className="h-3 w-3" />
                                        {copiedId ? "OK" : "Copy"}
                                    </button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-2 flex flex-wrap items-baseline rounded border border-slate-200/80 bg-slate-50/80 px-2 py-1.5 text-[10px] leading-snug">
                            {ticketMetaLineParts.map((part, i) => (
                                <React.Fragment key={part.key}>
                                    {i > 0 ? (
                                        <span
                                            className="mx-2 select-none font-light text-emerald-500"
                                            aria-hidden
                                        >
                                            ·
                                        </span>
                                    ) : null}
                                    <span className="font-bold text-slate-700">{part.label}</span>
                                    <span className={`ml-1 ${part.valueClass}`}>{part.value}</span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}

                <Formik
                    enableReinitialize
                    initialValues={
                        ticket
                            ? {
                                  category: ticketData?.category || "",
                                  category_other: ticketData?.category_other || "",
                                  priority: normalizePriorityForm(ticketData?.priority),
                                  subject: ticketData?.subject || "",
                                  description: ticketData?.description ?? "",
                                  assigned_to: ticketData?.assigned_to_id ?? "",
                                  order_id: ticketData?.order_id || "",
                                  order_item_id: ticketData?.order_item_id || "",
                                  time_slot_assigned: toDatetimeLocalValue(
                                      ticketData?.resolution_date_time_given
                                  ),
                                  image: null,
                              }
                            : {
                                  user: "",
                                  new_user_name: "",
                                  new_user_phone_number: "",
                                  subject: "",
                                  description: "",
                                  assigned_to: "",
                                  category: "",
                                  category_other: "",
                                  priority: "low",
                                  assigned_by: getUserId(),
                                  order_id: "",
                                  order_item_id: "",
                                  time_slot_assigned: "",
                                  image: null,
                              }
                    }
                    validationSchema={Yup.object({
                        user: Yup.string().when([], {
                            is: () => !ticket && isExistingUser,
                            then: (schema) => schema.required("Customer is required"),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                        new_user_name: Yup.string().when([], {
                            is: () => !ticket && !isExistingUser,
                            then: (schema) => schema.required("Name is required for new user"),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                        new_user_phone_number: Yup.string().when([], {
                            is: () => !ticket && !isExistingUser,
                            then: (schema) => schema.required("Phone number is required for new user"),
                            otherwise: (schema) => schema.notRequired(),
                        }),
                        subject: Yup.string().required("Subject is required"),
                        category: Yup.string().required("Category is required"),
                        category_other: Yup.string().notRequired(),
                        description: Yup.string().notRequired(),
                        priority: Yup.string().required("Priority is required"),
                    })}
                    onSubmit={handleSubmit}
                >
                    {({ setFieldValue, values }) => (
                        <Form>
                            {/* Customer Information */}
                            <div className="mb-2 rounded-lg border border-gray-200 bg-white p-2.5 shadow-sm">
                                {!ticket && (
                                    <div className="mb-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                                            <FiUser className="text-blue-500" />
                                            User Type
                                        </div>
                                        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsExistingUser(true);
                                                    setFieldValue("new_user_name", "");
                                                    setFieldValue("new_user_phone_number", "");
                                                }}
                                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${isExistingUser ? "bg-primary text-white shadow-sm" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                                            >
                                                Existing User
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsExistingUser(false);
                                                    setFieldValue("user", "");
                                                    setFieldValue("order_id", "");
                                                    setFieldValue("order_item_id", "");
                                                    setOrderOptions([]);
                                                    setOrderItemOptions([]);
                                                }}
                                                className={`px-3 py-1.5 text-xs font-medium transition-colors ${!isExistingUser ? "bg-primary text-white shadow-sm" : "bg-white text-gray-700 hover:bg-gray-50"}`}
                                            >
                                                New User
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {!ticket ? (
                                    <>
                                        <div className="grid gap-3 md:grid-cols-3">
                                            {isExistingUser ? (
                                                <AsyncFormikDropdown
                                                    label="Customer"
                                                    name="user"
                                                    placeholder="Search user by name / number"
                                                    apiCallFunction={async (input) => {
                                                        const response = await searchUser(input);
                                                        return (response?.data || []).map((person) => ({
                                                            value: person.id,
                                                            label: `${person.name} | ${person.phone_number}`,
                                                        }));
                                                    }}
                                                    onChange={(selectedOption) => {
                                                        const selectedUserId = selectedOption?.value || "";
                                                        setFieldValue("order_id", "");
                                                        setFieldValue("order_item_id", "");
                                                        setOrderItemOptions([]);
                                                        fetchConfirmedOrdersByUser(selectedUserId);
                                                    }}
                                                    required
                                                    needSearch={true}
                                                />
                                            ) : (
                                                <>
                                                    <InputField
                                                        label="New User Name"
                                                        name="new_user_name"
                                                        type="text"
                                                        placeholder="Enter name"
                                                        required
                                                    />
                                                    <InputField
                                                        label="New User Phone Number"
                                                        name="new_user_phone_number"
                                                        type="text"
                                                        placeholder="Enter phone number"
                                                        required

                                                        onChange={(e) => setFieldValue('new_user_phone_number', sanitizeMobile(e.target.value))}
                                                    />
                                                    <div />
                                                </>
                                            )}
                                        </div>
                                    </>
                                ) : null}

                                {ticket ? (
                                    <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                                        <div className="lg:col-span-2">
                                            <FormikDropdown
                                                label="Category"
                                                name="category"
                                                options={TICKET_CATEGORY_OPTIONS}
                                                placeholder="Select category"
                                                required
                                                height={36}
                                            />
                                        </div>
                                        {values.category === "other" ? (
                                            <div className="lg:col-span-2">
                                                <InputField
                                                    label="Other"
                                                    name="category_other"
                                                    type="text"
                                                    placeholder="Custom category"
                                                />
                                            </div>
                                        ) : null}
                                        <div className="lg:col-span-2">
                                            <FormikDropdown
                                                label="Priority"
                                                name="priority"
                                                options={TICKET_PRIORITY_OPTIONS}
                                                placeholder="Priority"
                                                required
                                                height={36}
                                            />
                                        </div>
                                        <div className="sm:col-span-2 lg:col-span-4">
                                            <InputField
                                                label="Problem"
                                                name="subject"
                                                type="text"
                                                placeholder="Issue title"
                                                required
                                            />
                                        </div>
                                        <div className="sm:col-span-2 lg:col-span-4">
                                            <TextField
                                                label="Description"
                                                name="description"
                                                placeholder="Details (optional)"
                                                rows={2}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <AsyncFormikDropdown
                                                label="Assigned to"
                                                name="assigned_to"
                                                placeholder="Search staff…"
                                                initialOptions={assigneeInitialOptions}
                                                apiCallFunction={async (input) => {
                                                    const response = await searchSupportAssignableUsers(input);
                                                    return (response?.data || []).map((user) => ({
                                                        value: user.id,
                                                        label: `${user.name} | ${user.mobile_number} | ${user.role}`,
                                                    }));
                                                }}
                                                needSearch={true}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <InputField
                                                label="Resolution slot"
                                                name="time_slot_assigned"
                                                type="datetime-local"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormikDropdown
                                                label="Order"
                                                name="order_id"
                                                options={orderOptions}
                                                placeholder={
                                                    ticketData?.user_id
                                                        ? "Order"
                                                        : "No customer"
                                                }
                                                height={36}
                                                onChange={(selectedOption) => {
                                                    const selectedOrderId = selectedOption?.value || "";
                                                    setFieldValue("order_item_id", "");
                                                    updateOrderItemsByOrderId(selectedOrderId);
                                                }}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <FormikDropdown
                                                label="Order item"
                                                name="order_item_id"
                                                options={orderItemOptions}
                                                placeholder="Line item"
                                                height={36}
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="mb-0.5 block text-xs font-medium text-gray-600">
                                                Image
                                            </label>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs file:mr-2 file:rounded file:border-0 file:bg-gray-100 file:px-2 file:py-1 file:text-[10px] file:font-medium"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0] || null;
                                                    setFieldValue("image", file);
                                                    if (file) {
                                                        setUploadedImagePreview(URL.createObjectURL(file));
                                                    } else {
                                                        setUploadedImagePreview("");
                                                    }
                                                }}
                                            />
                                        </div>
                                        {(uploadedImagePreview || selectedImage) && (
                                            <div className="h-20 w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50 sm:col-span-2">
                                                <img
                                                    src={uploadedImagePreview || selectedImage}
                                                    alt=""
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="mt-2 space-y-3">
                                        <h3 className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-1.5 text-sm font-semibold text-gray-800">
                                            <FiTag className="text-primary" />
                                            Ticket Information
                                        </h3>
                                        <div className="space-y-3">
                                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-sm">
                                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Details</h4>
                                                <div className="grid gap-3 md:grid-cols-3">
                                                    <FormikDropdown
                                                        label="Category"
                                                        name="category"
                                                        options={TICKET_CATEGORY_OPTIONS}
                                                        placeholder="Select category"
                                                        required
                                                    />
                                                    {values.category === "other" && (
                                                        <InputField
                                                            label="Other Category"
                                                            name="category_other"
                                                            type="text"
                                                            placeholder="Enter custom category"
                                                        />
                                                    )}

                                                    <FormikDropdown
                                                        label="Priority"
                                                        name="priority"
                                                        options={TICKET_PRIORITY_OPTIONS}
                                                        placeholder="Select priority"
                                                        required
                                                    />
                                                    <InputField
                                                        label="Problem"
                                                        name="subject"
                                                        type="text"
                                                        placeholder="Short issue title"
                                                        required
                                                    />

                                                    <div className="md:col-span-3">
                                                        <TextField
                                                            label="Description"
                                                            name="description"
                                                            placeholder="Describe the issue in detail"
                                                            rows={4}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Assignment</h4>
                                                <div className="grid gap-3 md:grid-cols-3">
                                                    <AsyncFormikDropdown
                                                        label="Assigned To"
                                                        name="assigned_to"
                                                        placeholder="Search staff/admin/manufacturer"
                                                        apiCallFunction={async (input) => {
                                                            const response = await searchSupportAssignableUsers(input);
                                                            return (response?.data || []).map((user) => ({
                                                                value: user.id,
                                                                label: `${user.name} | ${user.mobile_number} | ${user.role}`,
                                                            }));
                                                        }}
                                                        needSearch={true}
                                                    />

                                                    <InputField
                                                        label="Time Slot Assigned"
                                                        name="time_slot_assigned"
                                                        type="datetime-local"
                                                    />
                                                    <div />
                                                </div>
                                            </div>

                                            <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
                                                <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Order + Attachment</h4>
                                                <div className="grid gap-3 md:grid-cols-3">
                                                    <FormikDropdown
                                                        label="Order"
                                                        name="order_id"
                                                        options={orderOptions}
                                                        placeholder={isExistingUser ? "Select confirmed order" : "Select existing user first"}
                                                        onChange={(selectedOption) => {
                                                            const selectedOrderId = selectedOption?.value || "";
                                                            setFieldValue("order_item_id", "");
                                                            updateOrderItemsByOrderId(selectedOrderId);
                                                        }}
                                                    />

                                                    <FormikDropdown
                                                        label="Order Item"
                                                        name="order_item_id"
                                                        options={orderItemOptions}
                                                        placeholder="Select order item"
                                                    />
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Upload Image
                                                        </label>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white file:mr-3 file:rounded-md file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0] || null;
                                                                setFieldValue("image", file);
                                                                if (file) {
                                                                    setUploadedImagePreview(URL.createObjectURL(file));
                                                                } else {
                                                                    setUploadedImagePreview("");
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {(uploadedImagePreview || selectedImage) && (
                                                        <div className="h-28 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50 md:col-span-2">
                                                            <img
                                                                src={uploadedImagePreview || selectedImage}
                                                                alt="Ticket upload preview"
                                                                className="h-full w-full object-cover"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex justify-end border-t border-gray-100 pt-2">
                                <Button
                                    type="submit"
                                    className="rounded-lg bg-primary px-5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-600"
                                    children="Save"
                                />
                            </div>
                        </Form>
                    )}
                </Formik>

                {/* Timeline Section */}
                {ticket && (
                    <div className="relative mb-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                        <h3 className="mb-2 text-sm font-semibold text-gray-800">Timeline</h3>

                        <div className="mb-3 rounded-md border border-gray-100 bg-gray-50 p-2">
                            <Formik
                                initialValues={{ comment: "" }}
                                validationSchema={Yup.object({
                                    comment: Yup.string()
                                        .trim()
                                        .required("Comment is required"),
                                })}
                                onSubmit={(values, helpers) =>
                                    handleAddComment(values.comment, helpers)
                                }
                            >
                                {({ isSubmitting }) => (
                                    <Form className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                        <div className="min-w-0 flex-1">
                                            <TextField
                                                hideLabel
                                                label="Timeline note"
                                                name="comment"
                                                placeholder="Add a note…"
                                                rows={2}
                                                required
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="shrink-0 rounded-md bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-60"
                                        >
                                            {isSubmitting ? "…" : "Post"}
                                        </Button>
                                    </Form>
                                )}
                            </Formik>
                        </div>

                        {loading ? (
                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80">
                                <CustomLoading size={32} />
                            </div>
                        ) : (
                            <div className="relative pl-3">
                                <div className="absolute bottom-0 left-[11px] top-0 w-px bg-gray-200" />
                                <div className="space-y-3">
                                    {timelineData.map((item, index) => (
                                        <div
                                            key={item.id || index}
                                            className="relative flex gap-3"
                                        >
                                            <div
                                                className={`relative z-[1] mt-1 h-2.5 w-2.5 shrink-0 rounded-full border-2 border-white shadow ${
                                                    item.status === "Resolved"
                                                        ? "bg-green-500"
                                                        : item.status === "Pending"
                                                          ? "bg-amber-400"
                                                          : "bg-sky-500"
                                                }`}
                                            />
                                            <div className="min-w-0 flex-1 rounded border border-gray-100 bg-white px-2 py-1.5">
                                                <div className="flex flex-wrap items-baseline justify-between gap-1 text-[11px]">
                                                    <span
                                                        className={`font-semibold ${
                                                            item.status === "Resolved"
                                                                ? "text-green-800"
                                                                : item.status === "Pending"
                                                                  ? "text-amber-800"
                                                                  : "text-sky-800"
                                                        }`}
                                                    >
                                                        {item.status}
                                                    </span>
                                                    <span className="text-gray-400">
                                                        {formatDate(item.created_at)}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-[11px] leading-tight">
                                                    <span className="font-semibold text-slate-500">
                                                        Added by{" "}
                                                    </span>
                                                    <span className="font-semibold text-emerald-900">
                                                        {getTimelineStaffName(item)}
                                                    </span>
                                                </p>
                                                <p className="mt-0.5 text-xs leading-snug text-gray-700">
                                                    {item.comment}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default SupportTicketDetails;
